import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import CustomCalendar from './CustomCalendar';
import api from '../utils/api';
import { expandAllRecurringEvents } from '../utils/recurrence';
import '../styles/Calendar.css';

const Calendar = () => {
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [viewMode, setViewMode] = useState('day');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [employeesResponse, companyResponse, schedulesResponse, requestsResponse] = await Promise.all([
                    api.get('/api/users/employees'),
                    api.get('/api/companies/info'),
                    api.get('/api/schedules'),
                    api.get('/api/requests/company')
                ]);

                console.log('Raw API response:', employeesResponse.data);
                
                if (employeesResponse.data && Array.isArray(employeesResponse.data.employees)) {
                    setEmployees(employeesResponse.data.employees);
                } else {
                    console.error('Invalid employees data structure:', employeesResponse.data);
                }

                setCompanyInfo(companyResponse.data);

                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                employeesResponse.data.employees.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesResponse.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    const shiftDate = schedule.shift_date.split('T')[0];
                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                        files: schedule.files || [],
                        description: schedule.description || '',
                        location: schedule.location || '',
                        isAllDay: schedule.is_all_day || false,
                        color: schedule.color || '#4b8dff',
                        backgroundColor: schedule.color || '#4b8dff',
                        recurrence_rule: schedule.recurrence_rule || null
                    };
                });

                // Expand recurring events to show all instances
                const expandedSchedules = expandAllRecurringEvents(formattedSchedules);

                // Convert approved requests to calendar events
                const approvedRequests = requestsResponse.data.filter(request => request.status === 'approved');
                console.log('Approved requests in Calendar:', approvedRequests);

                const requestEvents = [];
                approvedRequests.forEach(request => {
                    const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                    if (!mappedEmployeeId) {
                        console.warn('No employee mapping found for user_id:', request.user_id);
                        return;
                    }

                    // Generate events for each day in the request range
                    const startDate = moment(request.start_date);
                    const endDate = moment(request.end_date);
                    const currentDate = startDate.clone();

                    while (currentDate.isSameOrBefore(endDate, 'day')) {
                        // Create PTO event as 8-hour block (9am-5pm)
                        const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                        const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                        // Determine title and color based on PTO status
                        let title, color, backgroundColor;
                        if (request.is_pto) {
                            title = `PTO - ${request.request_type.replace('_', ' ')}`;
                            color = '#10B981'; // Green for PTO
                            backgroundColor = '#10B981';
                        } else {
                            title = `Time Off - ${request.request_type.replace('_', ' ')}`;
                            color = '#F59E0B'; // Orange for regular time off
                            backgroundColor = '#F59E0B';
                        }

                        requestEvents.push({
                            id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                            calendarId: 'time-off',
                            title: title,
                            start: eventStart.toDate(),
                            end: eventEnd.toDate(),
                            resource: String(mappedEmployeeId),
                            color: color,
                            backgroundColor: backgroundColor,
                            description: request.reason || '',
                            isAllDay: false,
                            isPTO: request.is_pto || false,
                            requestType: request.request_type,
                            requestId: request.id,
                            countsTowardHours: request.is_pto || false,
                            files: []
                        });

                        currentDate.add(1, 'day');
                    }
                });

                // Combine schedules and request events
                const allEvents = [...expandedSchedules, ...requestEvents];
                console.log('All calendar events:', allEvents);
                setSchedules(allEvents);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to fetch calendar data');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleScheduleCreate = async (eventData, formData = null) => {
        try {
            // If formData is provided, we're handling file uploads
            if (formData) {
                console.log('📎 Creating schedule with file uploads');
                
                // Use the regular /api/schedules endpoint with FormData
                const response = await api.post('/api/schedules', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                console.log('Schedule with files created successfully:', response.data);
                
                const createdSchedule = {
                    id: response.data.id,
                    calendarId: response.data.type || 'tasks',
                    title: response.data.title,
                    start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                    end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                    resource: String(eventData.resource),
                    description: response.data.description,
                    location: response.data.location,
                    isAllDay: response.data.is_all_day,
                    files: response.data.files || []
                };

                setSchedules(prev => [...prev, createdSchedule]);
                return response.data.id;
            }

            // Regular schedule creation without files
            const shiftDate = moment(eventData.start).format('YYYY-MM-DD');
            const startTime = moment(eventData.start).format('HH:mm:ss');
            const endTime = moment(eventData.end).format('HH:mm:ss');

            // Create the base schedule data
            const scheduleData = {
                user_id: parseInt(eventData.resource),
                company_id: companyInfo.id,
                shift_date: shiftDate,
                start_time: startTime,
                end_time: endTime,
                title: eventData.title || 'Work Hours',
                calendar_type: 'shift',
                description: eventData.description || '',
                is_all_day: eventData.isAllDay || false,
                location: eventData.location || '',
                status: 'confirmed'
            };

            // Check if we have files to upload
            const hasFiles = eventData.files && eventData.files.length > 0;
            let response;

            if (hasFiles) {
                // If we have files, use FormData
                const fileFormData = new FormData();
                
                // Add each field from scheduleData
                Object.entries(scheduleData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        fileFormData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
                    }
                });

                // Add files
                eventData.files.forEach((file) => {
                    fileFormData.append('files', file);
                });

                response = await api.post('/api/schedules', fileFormData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                // If no files, use regular JSON
                response = await api.post('/api/schedules', scheduleData);
            }

            const createdSchedule = {
                id: response.data.id,
                calendarId: response.data.calendar_type || 'tasks',
                title: response.data.title,
                start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                resource: String(response.data.user_id),
                description: response.data.description,
                location: response.data.location,
                isAllDay: response.data.is_all_day,
                files: response.data.files
            };

            setSchedules(prev => [...prev, createdSchedule]);
            return response.data.id;
        } catch (error) {
            console.error('Error creating schedule:', error);
            throw error;
        }
    };

    if (loading) {
        return <div className="calendar-page loading">Loading...</div>;
    }

    if (error) {
        return <div className="calendar-page error">Error: {error}</div>;
    }

    return (
        <div className="calendar-page">
            <div className="calendar-header">
                <h1>Calendar</h1>
                <div className="calendar-actions">
                    <div className="view-toggle">
                        <button 
                            className={`toggle-button ${viewMode === 'day' ? 'active' : ''}`}
                            onClick={() => setViewMode('day')}
                        >
                            Day
                        </button>
                        <button 
                            className={`toggle-button ${viewMode === 'week' ? 'active' : ''}`}
                            onClick={() => setViewMode('week')}
                        >
                            Week
                        </button>
                    </div>
                    <button 
                        className="hours-summary-button"
                        onClick={() => navigate('/hours-summary')}
                    >
                        View Hours Summary
                    </button>
                </div>
            </div>

            <div className="calendar-content">
                <CustomCalendar
                    employees={employees}
                    schedules={schedules}
                    onScheduleCreate={handleScheduleCreate}
                    viewMode={viewMode}
                />
            </div>
        </div>
    );
};

export default Calendar; 